import * as _ from 'underscore';

import { Injectable } from '@angular/core';

import {
	CategoriesConfig,
	ChartConfig,
	ChartDataConfig,
	ChartLegendConfig,
	GraphConfig,
	SeriesConfig,
	XAxisConfig,
	YAxisConfig
} from '../interfaces/chart.interface';

/**
 *
 */
@Injectable()
export class ChartConfigurationMapperService {
	/**
	 *
	 * @param chartConfig
	 * @returns {ChartConfig}
	 */
	mapChartConfigToInterface(chartConfig: any): ChartConfig {
		const chartConfiguration: ChartConfig = {} as ChartConfig;
		chartConfiguration.categoriesConfig = chartConfig.categories
			? this.mapCategoriesResponseToInterface(chartConfig.categories)
			: ({} as CategoriesConfig);
		chartConfiguration.graphConfig = chartConfig.graphConfig
			? this.mapGraphConfigToInterface(chartConfig.graphConfig)
			: ({} as GraphConfig);
		chartConfiguration.enableYAxisPlotlines = chartConfig.enableYAxisPlotlines;
		chartConfiguration.enableYAxisTitle = chartConfig.enableYAxisTitle;
		chartConfiguration.pointsCount = chartConfig.pointsCount;
		chartConfiguration.stepCount = chartConfig.stepCount;
		chartConfiguration.marginLeft = chartConfig.marginLeft;
		chartConfiguration.height = chartConfig.height;
		chartConfiguration.labelsStep = chartConfig.labelsStep;
		chartConfiguration.enableXAxisPlotlines = chartConfig.enableXAxisPlotlines;
		const seriesData = chartConfig.series ? this.mapSeriesResponseToInterface(chartConfig.series) : [];
		chartConfiguration.seriesConfig = _.sortBy(seriesData, 'order');
		chartConfiguration.chartDataConfig = chartConfig.dataConfig
			? this.mapChartDataResponseToInterface(chartConfig.dataConfig)
			: ([] as ChartDataConfig[]);
		chartConfiguration.backgroundColor = chartConfig.backgroundColor;
		return chartConfiguration;
	}

	/**
	 *
	 * @param graphResponse
	 * @returns {GraphConfig}
	 */
	mapGraphConfigToInterface(graphResponse: any): GraphConfig {
		const graphConfig: GraphConfig = {} as GraphConfig;
		graphConfig.graphTitle = graphResponse.graphTitle;
		graphConfig.margin = graphResponse.margin;
		graphConfig.graphSubTitle = graphResponse.graphSubTitle;
		graphConfig.xAxisLabel = graphResponse.xAxisLabel;
		graphConfig.stacking = graphResponse.stacking;
		graphConfig.graphType = graphResponse.graphType;
		graphConfig.legendConfig = this.mapLegendConfigToInterface(graphResponse.legendConfig);
		graphConfig.xAxisConfig = graphResponse.xAxisConfig
			? this.mapXAxisConfigToInterface(graphResponse.xAxisConfig)
			: ({} as XAxisConfig);
		graphConfig.yAxis = this.mapYAxisConfigurations(graphResponse);
		graphConfig.primaryYAxis = this.mapYAxisConfigToInterface(graphResponse, false);
		graphConfig.secondaryYAxis = this.mapYAxisConfigToInterface(graphResponse, true);
		graphConfig.startAngle = graphResponse.startAngle;
		graphConfig.endAngle = graphResponse.endAngle;
		graphConfig.margin = graphResponse.margin;
		graphConfig.enableGraphNavigation = graphResponse.enableGraphNavigation;
		graphConfig.tickmarkPlacement = graphResponse.tickmarkPlacement;
		graphConfig.tickWidth = graphResponse.tickWidth;
		graphConfig.tickColor = graphResponse.tickColor;
		graphConfig.xaxisLabely = graphResponse.xaxisLabely;
		graphConfig.customTooltip = graphResponse.customTooltip;
		graphConfig.xAxisLabelStyle = graphResponse.xAxisLabelStyle;
		graphConfig.ignoreHiddenSeries = graphResponse.ignoreHiddenSeries;
		graphConfig.plotBorderWidth = graphResponse.plotBorderWidth;
		graphConfig.alignTicks = graphResponse.alignTicks;
		graphConfig.borderRadius = graphResponse.borderRadius;
		graphConfig.pointWidth = graphResponse.pointWidth;
		return graphConfig;
	}

	/**
	 *
	 * @param legendResponse
	 * @returns {ChartLegendConfig}
	 */
	mapLegendConfigToInterface(legendResponse: any): ChartLegendConfig {
		const legendConfig: ChartLegendConfig = legendResponse as ChartLegendConfig;
		return legendConfig;
	}

	/**
	 *
	 * @param legendResponse
	 * @returns {ChartLegendConfig}
	 */
	mapXAxisConfigToInterface(xAxisConfigResponse: any): XAxisConfig {
		const xAxisConfig: XAxisConfig = xAxisConfigResponse as XAxisConfig;
		return xAxisConfig;
	}

	/**
	 *
	 * @param yAxisResponse
	 * @returns {YAxisConfig[]}
	 */
	mapYAxisConfigurations(yAxisResponse: any): YAxisConfig[] {
		const yAxisConfigs: YAxisConfig[] = [];

		const primaryYAxis: YAxisConfig = this.mapYAxisConfigToInterface(yAxisResponse, false);
		const secondaryYAxis: YAxisConfig = this.mapYAxisConfigToInterface(yAxisResponse, true);

		yAxisConfigs.push(primaryYAxis);
		yAxisConfigs.push(secondaryYAxis);

		return yAxisConfigs;
	}

	/**
	 *
	 * @param yAxisResponse
	 * @param {boolean} isOpposite
	 * @returns {YAxisConfig}
	 */
	mapYAxisConfigToInterface(yAxisResponse: any, isOpposite: boolean): YAxisConfig {
		const yAxisConfig: YAxisConfig = {} as YAxisConfig;
		yAxisConfig.title = isOpposite ? { text: yAxisResponse.yAxisLabelRight } : { text: yAxisResponse.yAxisLabelLeft };
		yAxisConfig.className = isOpposite ? 'secondary-axis' : 'primary-axis';
		yAxisConfig.allowDecimals = yAxisResponse.allowDecimals;
		yAxisConfig.gridLineStyle = yAxisResponse.gridLineStyle;
		yAxisConfig.gridLineWidth = yAxisResponse.gridLineWidth;
		yAxisConfig.opposite = isOpposite;
		if (yAxisResponse.yAxisLabelStyle) {
			yAxisConfig.title.style = yAxisResponse.yAxisLabelStyle;
			yAxisConfig.labels = {
				style: yAxisResponse.yAxisLabelStyle
			};
		}
		return yAxisConfig;
	}

	/**
	 *
	 * @param {any[]} chartDataResponseArray
	 * @returns {ChartDataConfig[]}
	 */
	mapChartDataResponseToInterface(chartDataResponseArray: any[]): ChartDataConfig[] {
		const chartDataConfigs: ChartDataConfig[] = [];
		for (const chartDataResponse of chartDataResponseArray) {
			const chartResponseConfig: ChartDataConfig = chartDataResponse as ChartDataConfig;
			chartDataConfigs.push(chartResponseConfig);
		}
		return chartDataConfigs;
	}

	/**
	 *
	 * @param {any[]} seriesDataResponseArray
	 * @returns {SeriesConfig[]}
	 */
	mapSeriesResponseToInterface(seriesDataResponseArray: any[]): SeriesConfig[] {
		const seriesDataConfigs: SeriesConfig[] = [];
		for (const seriesDataResponse of seriesDataResponseArray) {
			const seriesConfig: SeriesConfig = seriesDataResponse as SeriesConfig;
			seriesDataConfigs.push(seriesConfig);
		}
		return seriesDataConfigs;
	}

	/**
	 *
	 * @param categoriesResponse
	 * @returns {CategoriesConfig}
	 */
	mapCategoriesResponseToInterface(categoriesResponse: any): CategoriesConfig {
		const categoriesConfig: CategoriesConfig = categoriesResponse as CategoriesConfig;
		return categoriesConfig;
	}
}
